import { S3Client, PutObjectCommand, GetObjectCommand, DeleteObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';

// S3 configuration from environment variables
const S3_ACCESS_KEY_ID = import.meta.env.VITE_SUPABASE_STORAGE_S3_ACCESS_KEY_ID;
const S3_SECRET_ACCESS_KEY = import.meta.env.VITE_SUPABASE_STORAGE_S3_SECRET_ACCESS_KEY;
const S3_REGION = 'us-east-1'; // Supabase uses us-east-1 for storage
const S3_ENDPOINT = import.meta.env.VITE_SUPABASE_URL ? 
  `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/s3` : '';

class S3StorageService {
  private s3Client: S3Client | null = null;
  private bucketName = 'cached-images';

  constructor() {
    this.initializeS3Client();
  }

  private initializeS3Client() {
    if (!S3_ACCESS_KEY_ID || !S3_SECRET_ACCESS_KEY || !S3_ENDPOINT) {
      console.warn('[S3Storage] Missing S3 credentials or endpoint');
      return;
    }

    try {
      // Extract project ID from Supabase URL
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const projectId = supabaseUrl?.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1];
      
      if (!projectId) {
        console.warn('[S3Storage] Could not extract project ID from Supabase URL');
        return;
      }

      this.s3Client = new S3Client({
        region: S3_REGION,
        endpoint: S3_ENDPOINT,
        credentials: {
          accessKeyId: S3_ACCESS_KEY_ID,
          secretAccessKey: S3_SECRET_ACCESS_KEY,
        },
        forcePathStyle: true, // Required for Supabase S3 compatibility
      });

      console.log('[S3Storage] S3 client initialized successfully');
    } catch (error) {
      console.error('[S3Storage] Failed to initialize S3 client:', error);
    }
  }

  /**
   * Upload a file directly to S3
   */
  async uploadFile(key: string, file: Blob | Buffer, contentType?: string): Promise<string | null> {
    if (!this.s3Client) {
      console.error('[S3Storage] S3 client not initialized');
      return null;
    }

    try {
      const command = new PutObjectCommand({
        Bucket: this.bucketName,
        Key: key,
        Body: file,
        ContentType: contentType || 'image/png',
      });

      await this.s3Client.send(command);
      
      // Return the public URL
      const publicUrl = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/${this.bucketName}/${key}`;
      console.log(`[S3Storage] File uploaded successfully: ${publicUrl}`);
      return publicUrl;
    } catch (error) {
      console.error('[S3Storage] Upload failed:', error);
      return null;
    }
  }

  /**
   * Generate a presigned URL for uploading
   */
  async getPresignedUploadUrl(key: string, expiresIn: number = 3600): Promise<string | null> {
    if (!this.s3Client) {
      console.error('[S3Storage] S3 client not initialized');
      return null;
    }

    try {
      const command = new PutObjectCommand({
        Bucket: this.bucketName,
        Key: key,
      });

      const url = await getSignedUrl(this.s3Client, command, { expiresIn });
      console.log(`[S3Storage] Generated presigned upload URL for ${key}`);
      return url;
    } catch (error) {
      console.error('[S3Storage] Failed to generate presigned URL:', error);
      return null;
    }
  }

  /**
   * Check if S3 is available
   */
  isAvailable(): boolean {
    return this.s3Client !== null;
  }

  /**
   * Delete a file from S3
   */
  async deleteFile(key: string): Promise<boolean> {
    if (!this.s3Client) {
      console.error('[S3Storage] S3 client not initialized');
      return false;
    }

    try {
      const command = new DeleteObjectCommand({
        Bucket: this.bucketName,
        Key: key,
      });

      await this.s3Client.send(command);
      console.log(`[S3Storage] File deleted successfully: ${key}`);
      return true;
    } catch (error) {
      console.error('[S3Storage] Delete failed:', error);
      return false;
    }
  }
}

export const s3StorageService = new S3StorageService();